import { useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { PlusOutlined, ProjectOutlined, SearchOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { projectsApi } from '@/api/projects'
import type { ListProjectsQuery, Project } from '@/types/api'

const { Title, Text } = Typography

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function dashId(value: number | null | undefined) {
  return value == null ? <Text type="secondary">—</Text> : value
}

export function ProjectListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()
  const [filterForm] = Form.useForm<ListProjectsQuery>()
  const [filters, setFilters] = useState<ListProjectsQuery>({})

  const hasFilters = Object.values(filters).some((v) => v != null && v !== '')

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', filters],
    queryFn: () => projectsApi.list(filters),
  })

  const createMutation = useMutation({
    mutationFn: projectsApi.create,
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      setModalOpen(false)
      form.resetFields()
      navigate(`/projects/${project.id}`)
    },
  })

  const applyFilters = (values: ListProjectsQuery) => {
    const next: ListProjectsQuery = {}
    const id = String(values.id || '').trim()
    if (id) next.id = id
    if (values.pid != null) next.pid = values.pid
    if (values.shop_type_id != null) next.shop_type_id = values.shop_type_id
    if (values.chain_brand_id != null) next.chain_brand_id = values.chain_brand_id
    if (values.inventory_mould_id != null) next.inventory_mould_id = values.inventory_mould_id
    if (values.inventory_id != null) next.inventory_id = values.inventory_id
    setFilters(next)
  }

  const emptyNoProjects = !isLoading && !hasFilters && (projects || []).length === 0

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <ProjectOutlined /> 项目列表
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          新建项目
        </Button>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form
          form={filterForm}
          layout="inline"
          onFinish={applyFilters}
          style={{ rowGap: 12 }}
        >
          <Form.Item
            name="id"
            label="主键 UUID"
            rules={[
              {
                validator: async (_, value) => {
                  const text = String(value || '').trim()
                  if (!text || UUID_RE.test(text)) return
                  throw new Error('请输入完整 UUID')
                },
              },
            ]}
          >
            <Input placeholder="cad_project.id" allowClear style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="pid" label="设计施工项目">
            <InputNumber placeholder="pid" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="shop_type_id" label="店铺类型">
            <InputNumber placeholder="shop_type_id" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="chain_brand_id" label="装企品牌">
            <InputNumber placeholder="chain_brand_id" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="inventory_mould_id" label="清单模板">
            <InputNumber placeholder="inventory_mould_id" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="inventory_id" label="清单">
            <InputNumber placeholder="inventory_id" style={{ width: 140 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields()
                  setFilters({})
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {emptyNoProjects ? (
        <Card>
          <Empty
            description="还没有项目，先创建一个"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => setModalOpen(true)}>
              创建第一个项目
            </Button>
          </Empty>
        </Card>
      ) : (
        <Table<Project>
          loading={isLoading}
          dataSource={projects || []}
          rowKey="id"
          scroll={{ x: 1280 }}
          locale={{ emptyText: hasFilters ? '没有符合条件的项目' : '暂无项目' }}
          onRow={(record) => ({
            onClick: () => navigate(`/projects/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '项目名',
              dataIndex: 'name',
              key: 'name',
              render: (name) => <Text strong>{name}</Text>,
            },
            {
              title: '品牌',
              dataIndex: 'brand_id',
              key: 'brand_id',
              render: (b) => <Tag color="blue">{b || 'generic'}</Tag>,
              width: 100,
            },
            {
              title: '模板版本',
              dataIndex: 'template_version',
              key: 'template_version',
              width: 100,
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              render: (s) => <Tag color="green">{s}</Tag>,
              width: 80,
            },
            {
              title: '主键 UUID',
              dataIndex: 'id',
              key: 'id',
              width: 120,
              render: (id: string) => (
                <Text code style={{ fontSize: 11 }} title={id}>
                  {id.slice(0, 8)}
                </Text>
              ),
            },
            {
              title: '设计施工项目',
              dataIndex: 'pid',
              key: 'pid',
              width: 120,
              render: dashId,
            },
            {
              title: '店铺类型',
              dataIndex: 'shop_type_id',
              key: 'shop_type_id',
              width: 100,
              render: dashId,
            },
            {
              title: '装企品牌',
              dataIndex: 'chain_brand_id',
              key: 'chain_brand_id',
              width: 100,
              render: dashId,
            },
            {
              title: '清单模板',
              dataIndex: 'inventory_mould_id',
              key: 'inventory_mould_id',
              width: 100,
              render: dashId,
            },
            {
              title: '清单',
              dataIndex: 'inventory_id',
              key: 'inventory_id',
              width: 90,
              render: dashId,
            },
            {
              title: '项目参数（层高等）',
              dataIndex: 'param_overrides',
              key: 'params',
              render: (p: Record<string, number | string>) =>
                Object.keys(p).length > 0 ? (
                  <Space size="small" wrap>
                    {Object.entries(p).map(([k, v]) => (
                      <Tag key={k}>
                        {k}={v}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">未设置</Text>
                ),
            },
          ]}
        />
      )}

      <Modal
        title="新建项目"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        okText="创建"
        cancelText="取消"
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            const paramOverrides: Record<string, number> = {}
            if (values.floor_height_m) paramOverrides.floor_height_m = values.floor_height_m
            createMutation.mutate({
              name: values.name,
              param_overrides: paramOverrides,
            })
          }}
          initialValues={{
            floor_height_m: 3.0,
          }}
        >
          <Form.Item
            label="项目名"
            name="name"
            rules={[{ required: true, message: '请输入项目名' }]}
          >
            <Input placeholder="如：麦当劳领秀国际店" />
          </Form.Item>

          <Title level={5}>项目级参数（可在复核阶段按空间覆盖）</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            层高无系统默认，未填写则须在复核补齐。厨房/卫生间防水高度默认 1.8m、墙砖高度默认 2.5m。
          </Text>

          <Form.Item
            label="项目层高（米）"
            name="floor_height_m"
            help="所有空间默认层高。特殊空间可在复核页覆盖"
          >
            <InputNumber min={1.5} max={10} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
